import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, FloppyDisk } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MB_KEYS = ['MB1', 'MB2', 'MB3', 'MB4', 'MB5', 'MB6'];

const MBConfig = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mbKeys, setMbKeys] = useState(['MB1', 'MB2', 'MB3', 'MB4', 'MB5', 'MB6']);
  const [labels, setLabels] = useState({});
  const [defaults, setDefaults] = useState({});
  const [handlers, setHandlers] = useState([]);
  const [autoAssignEnabled, setAutoAssignEnabled] = useState(false);
  const [autoAssignMb, setAutoAssignMb] = useState('');
  const [savingAutoAssign, setSavingAutoAssign] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, handlersRes] = await Promise.all([
        axios.get(`${API}/mb-config`),
        axios.get(`${API}/users/handlers`),
      ]);
      setLabels(cfg.data.labels || {});
      setDefaults(cfg.data.default_handlers || {});
      setAutoAssignEnabled(Boolean(cfg.data.auto_assign_mb?.enabled));
      setAutoAssignMb(cfg.data.auto_assign_mb?.mb_id || '');
      if (cfg.data.mbs) {
        setMbKeys(cfg.data.mbs.filter(m => !m.hidden).map(m => m.id));
      }
      setHandlers(Array.isArray(handlersRes.data) ? handlersRes.data : (handlersRes.data?.handlers || []));
    } catch (e) {
      toast.error('Failed to load MB config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/mb-config`, {
        actor_id: user?.id,
        labels,
        default_handlers: defaults,
      });
      // Cache labels in localStorage so other pages can read them without extra API call
      try {
        localStorage.setItem('mb_labels', JSON.stringify(labels));
        window.dispatchEvent(new Event('mb-labels-updated'));
      } catch {}
      toast.success('MB config saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveAutoAssign = async () => {
    setSavingAutoAssign(true);
    try {
      const response = await axios.patch(`${API}/mb-config/auto-assign`, {
        enabled: autoAssignEnabled,
        mb_id: autoAssignEnabled && autoAssignMb ? autoAssignMb : null
      });
      setAutoAssignEnabled(Boolean(response.data.auto_assign_mb?.enabled));
      setAutoAssignMb(response.data.auto_assign_mb?.mb_id || '');
      toast.success('Auto-assign MB setting saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save auto-assign setting');
    } finally {
      setSavingAutoAssign(false);
    }
  };

  if (loading) return <div className="h-screen overflow-y-auto p-8 text-center text-gray-500">Loading...</div>;

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden" data-testid="mb-config-page">
      {/* Header */}
      <div className="bg-wa-primary-strong text-white px-4 py-3 flex items-center gap-3 shadow">
        <button onClick={() => navigate('/admin-settings')} className="p-1.5 rounded-full hover:bg-white/20" data-testid="back-btn">
          <ArrowLeft size={22} />
        </button>
        <div className="flex-1">
          <h1 className="text-lg font-bold">MB Setup Configuration</h1>
          <p className="text-xs opacity-90">Rename setups & auto-route clients to default handler</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto p-4 space-y-3 pb-24">
        <div className="bg-wa-hover border border-blue-200 rounded-lg px-3 py-2 text-xs text-wa-text-primary">
          💡 यहाँ आप हर MB को custom नाम (जैसे "USDT", "UPI Fast") दे सकते हैं और हर MB का default handler set कर सकते हैं। जब कोई client उस MB में assign होगा, उसकी सारी active chats automatically उस handler को assign हो जाएँगी।
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-emerald-200 p-4 space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-sm font-bold leading-5 text-slate-900">Auto-assign MB for new users</h2>
              <p className="mt-0.5 text-[11px] leading-4 text-slate-500">New customers, handlers, and admins receive this MB when they are created.</p>
            </div>
            <button
              type="button"
              onClick={() => setAutoAssignEnabled((value) => !value)}
              className={`relative mt-0.5 h-7 w-12 shrink-0 rounded-full border transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:ring-offset-1 ${autoAssignEnabled ? 'border-emerald-600 bg-emerald-600' : 'border-slate-300 bg-slate-300'}`}
              aria-label="Toggle auto MB assignment"
            >
              <span className={`absolute left-1 top-1 h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${autoAssignEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>
          <label className="block text-[11px] font-semibold text-slate-600">Default MB</label>
          <select
            value={autoAssignMb}
            onChange={(e) => setAutoAssignMb(e.target.value)}
            disabled={!autoAssignEnabled}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
            data-testid="auto-assign-mb"
          >
            <option value="">None — leave unassigned (N)</option>
            {mbKeys.map((mb) => <option key={mb} value={mb}>{labels[mb] || mb}</option>)}
          </select>
          <button
            type="button"
            onClick={saveAutoAssign}
            disabled={savingAutoAssign}
            className="w-full rounded-lg bg-emerald-600 px-3 py-2 text-sm font-bold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
            data-testid="save-auto-assign-mb"
          >
            {savingAutoAssign ? 'Saving Auto-assign…' : 'Save Auto-assign MB'}
          </button>
        </div>

        {mbKeys.map((mb) => (
          <div key={mb} className="bg-white rounded-lg shadow-sm border p-3 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono px-2 py-0.5 bg-gray-100 rounded-full text-gray-600">{mb}</span>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-gray-600">Custom Label:</label>
              <input
                type="text"
                value={labels[mb] || ''}
                onChange={(e) => setLabels((p) => ({ ...p, [mb]: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm border rounded-md focus:ring-2 focus:ring-teal-500 outline-none"
                placeholder={mb}
                data-testid={`label-${mb}`}
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-gray-600">Default Handler (auto-route clients):</label>
              <select
                value={defaults[mb] || ''}
                onChange={(e) => setDefaults((p) => ({ ...p, [mb]: e.target.value || null }))}
                className="w-full px-2 py-1.5 text-sm border rounded-md focus:ring-2 focus:ring-teal-500 outline-none bg-white"
                data-testid={`default-${mb}`}
              >
                <option value="">— No auto-route —</option>
                {handlers.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.username} ({(h.role || '').replace('_', ' ')})
                  </option>
                ))}
              </select>
            </div>
          </div>
        ))}

          <button
            onClick={save}
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-teal-600 text-white font-bold hover:bg-teal-700 disabled:opacity-50"
            data-testid="save-mb-config"
          >
            <FloppyDisk size={20} /> {saving ? 'Saving...' : 'Save MB Configuration'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MBConfig;
